/** Hooks for the loan-request workflow: creating, approving, rejecting, and cancelling borrow requests. */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { captureSentryException } from '@/lib/sentry';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { mapSupabaseError } from '@/lib/errorMapper';
import { assertOnline } from '@/lib/networkGuard';
import { toast } from 'sonner';
import posthog from '@/lib/posthog';

export interface LoanRequest {
  id: string;
  item_id: string;
  requester_id: string;
  approver_id: string;
  status: 'pending' | 'approved' | 'rejected';
  message: string | null;
  created_at: string;
  responded_at: string | null;
  rejection_reason: string | null;
}

export interface LoanRequestWithDetails extends LoanRequest {
  item_name?: string;
  requester_name?: string;
  approver_name?: string;
}

/**
 * Returns all loan requests made by the current user, enriched with item and approver names.
 *
 * @returns TanStack query result containing an array of {@link LoanRequestWithDetails}.
 */
export function useMyLoanRequests() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['loan-requests', 'mine', user?.id],
    queryFn: async (): Promise<LoanRequestWithDetails[]> => {
      if (!user) return [];

      const { data: requests, error } = await supabase
        .from('loan_requests')
        .select('*')
        .eq('requester_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!requests || requests.length === 0) return [];

      // Fetch item names and approver profiles in parallel
      const itemIds = [...new Set(requests.map((r) => r.item_id))];
      const approverIds = [...new Set(requests.map((r) => r.approver_id))];
      const [{ data: items, error: itemsError }, { data: profiles, error: profilesError }] =
        await Promise.all([
          supabase.from('items').select('id, name').in('id', itemIds),
          supabase.from('profiles').select('user_id, display_name').in('user_id', approverIds),
        ]);

      if (itemsError) throw itemsError;
      if (profilesError) throw profilesError;

      const itemMap = new Map(items?.map((i) => [i.id, i.name]) || []);
      const profileMap = new Map(profiles?.map((p) => [p.user_id, p.display_name]) || []);

      return requests.map((r) => ({
        ...r,
        status: r.status as 'pending' | 'approved' | 'rejected',
        item_name: itemMap.get(r.item_id) || 'Unknown Item',
        approver_name: profileMap.get(r.approver_id) || 'Unknown User',
      }));
    },
    enabled: !!user,
  });
}

/**
 * Returns pending loan requests where the current user is the designated approver.
 *
 * `approver_id` is set at request-creation time by {@link useCreateLoanRequest},
 * based on the item's circulation policy: the item owner for
 * `RETURN_TO_OWNER_REQUIRED` items, or the current holder for
 * `FREE_CIRCULATION_ALLOWED` items.
 *
 * @returns TanStack query result containing an array of {@link LoanRequestWithDetails}.
 *   Polls every 60 seconds and refetches on window focus.
 */
export function usePendingRequestsForMe() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['loan-requests', 'pending-for-me', user?.id],
    queryFn: async (): Promise<LoanRequestWithDetails[]> => {
      if (!user) return [];

      const { data: requests, error } = await supabase
        .from('loan_requests')
        .select('*')
        .eq('approver_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!requests || requests.length === 0) return [];

      // Fetch item names and requester profiles in parallel
      const itemIds = [...new Set(requests.map((r) => r.item_id))];
      const requesterIds = [...new Set(requests.map((r) => r.requester_id))];
      const [{ data: items, error: itemsError }, { data: profiles, error: profilesError }] =
        await Promise.all([
          supabase.from('items').select('id, name').in('id', itemIds),
          supabase.from('profiles').select('user_id, display_name').in('user_id', requesterIds),
        ]);

      if (itemsError) throw itemsError;
      if (profilesError) throw profilesError;

      const itemMap = new Map(items?.map((i) => [i.id, i.name]) || []);
      const profileMap = new Map(profiles?.map((p) => [p.user_id, p.display_name]) || []);

      return requests.map((r) => ({
        ...r,
        status: r.status as 'pending' | 'approved' | 'rejected',
        item_name: itemMap.get(r.item_id) || 'Unknown Item',
        requester_name: profileMap.get(r.requester_id) || 'Unknown User',
      }));
    },
    enabled: !!user,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Submits a new borrow request for an item.
 *
 * The approver is determined by the caller based on the item's circulation policy.
 * On success, invalidates loan-request and notification queries and captures an
 * analytics event.
 *
 * @returns TanStack mutation accepting `{ itemId, approverId, message?, groupId? }`.
 * @throws On network offline or Supabase insert error (surfaced via toast).
 */
export function useCreateLoanRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      itemId,
      approverId,
      message,
      groupId,
    }: {
      itemId: string;
      approverId: string;
      message?: string;
      groupId?: string;
    }) => {
      await assertOnline();

      if (!user) {
        throw new Error('You must be signed in to request a loan.');
      }

      const { data, error } = await supabase
        .from('loan_requests')
        .insert({
          item_id: itemId,
          requester_id: user.id,
          approver_id: approverId,
          message,
          group_id: groupId ?? null,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['loan-requests'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      posthog.capture('loan_request_submitted', { item_id: variables.itemId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/** Sentinel message used when a request is responded to more than once (race condition). */
export const REQUEST_CONFLICT_MESSAGE = 'Request was already processed.';

/**
 * Approves or rejects a pending loan request.
 *
 * **Approval path:** Calls the `approve_loan_request` RPC, which atomically
 * verifies the request is still pending, creates the loan, and marks the request
 * as approved — all in one DB transaction. If the RPC fails due to a
 * circulation-policy violation, the request is auto-rejected with an explanatory
 * reason so the requester can resubmit to the correct holder.
 *
 * **Rejection path:** A plain UPDATE — no loan exists to clean up. The
 * `.eq('status', 'pending')` guard returns zero rows if the request was already
 * processed concurrently, surfaced as {@link REQUEST_CONFLICT_MESSAGE}.
 *
 * @returns TanStack mutation accepting `{ requestId, approved, dueAt? }`.
 * @throws {Error} With {@link REQUEST_CONFLICT_MESSAGE} if the request was already processed.
 * @throws {Error} With a user-facing message on circulation-policy violations (auto-rejects the request).
 */
export function useRespondToRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requestId,
      approved,
      dueAt,
      note,
    }: {
      requestId: string;
      approved: boolean;
      dueAt?: string;
      note?: string;
    }) => {
      await assertOnline();
      const now = new Date().toISOString();

      if (approved) {
        // Single RPC: verify-pending + create-loan + mark-approved run inside
        // one transaction, so a partial-success state (loan exists but request
        // still pending) can never occur.
        const { error: approveError } = await supabase.rpc('approve_loan_request' as any, {
          p_request_id: requestId,
          p_due_at: dueAt ?? null,
          p_note: note?.trim() || null,
        });

        if (approveError) {
          if (approveError.message?.includes('no longer pending')) {
            throw new Error(REQUEST_CONFLICT_MESSAGE);
          }

          // Circulation-policy violation: the loan was never created, so the
          // request is safe to auto-reject.  This is a best-effort single
          // UPDATE; if it fails the approver can still reject manually.
          // Match on the structured DETAIL code first; fall back to message text
          // so local/older DB versions without the structured codes still work.
          // [LEARN] Supabase wraps DB RAISE EXCEPTION details in `.details` — casting to `any` lets us access this untyped property to extract structured error codes.
          const errorDetails = (approveError as any).details ?? '';
          const errorMessage = approveError.message ?? '';

          // Handle approve_loan_request's own validation errors (not
          // circulation-policy failures). These should NOT auto-reject the
          // request — they are approver-side problems.
          if (errorMessage.includes('Request not found')) {
            throw new Error('This request no longer exists.');
          }
          if (errorMessage.includes('Only the approver')) {
            throw new Error('You are not authorised to approve this request.');
          }

          // Map known error codes to user-facing rejection reasons
          // and approver-facing messages.
          const circulationErrors: Record<string, { rejection: string; approver: string }> = {
            OWNER_ONLY: {
              rejection:
                'This item was returned to the owner before your request could be approved. Please submit a new request to the owner.',
              approver:
                'This item has been returned to the owner since the request was made. The request has been auto-rejected — the requester can submit a new request to the owner.',
            },
            HOLDER_ONLY: {
              rejection:
                'This item is currently on loan to another member. Please try again once the item is returned, or submit a new request to the current holder.',
              approver:
                'This item has an active loan to another member. The request has been auto-rejected — the requester can try again once the item is returned.',
            },
            RETURN_TO_OWNER_REQUIRED: {
              rejection:
                'This item must be returned to the owner before it can be lent again. Please try again later.',
              approver:
                'This item must be returned to the owner first. The request has been auto-rejected.',
            },
            ALREADY_ON_LOAN: {
              rejection:
                'This item is no longer available. Please try again once the current loan is completed.',
              approver: 'This item is no longer available. The request has been auto-rejected.',
            },
            NOT_AVAILABLE: {
              rejection: 'This item is no longer available for loan.',
              approver: 'This item is no longer available. The request has been auto-rejected.',
            },
            NOT_GROUP_MEMBER: {
              rejection:
                'You are not a member of a group containing this item. Please join a group with this item and try again.',
              approver:
                'The borrower is not a member of a group containing this item. The request has been auto-rejected.',
            },
            SELF_LOAN: {
              rejection: 'You cannot borrow your own item.',
              approver:
                'Cannot create a loan to the item owner. The request has been auto-rejected.',
            },
            ITEM_NOT_FOUND: {
              rejection: 'This item could not be found. It may have been deleted.',
              approver:
                'The item could not be found — it may have been deleted. The request has been auto-rejected.',
            },
          };

          const matchedCode = Object.keys(circulationErrors).find(
            (code) => errorDetails.includes(code) || errorMessage.includes(code),
          );
          // Fall back to message-text matching for older DB versions without structured codes
          const isOwnerOnlyFallback =
            !matchedCode && errorMessage.includes('Only the owner can lend');
          const isRateLimited = !matchedCode && errorMessage.includes('Rate limit exceeded');
          const matched = matchedCode
            ? circulationErrors[matchedCode]
            : isOwnerOnlyFallback
              ? circulationErrors.OWNER_ONLY
              : isRateLimited
                ? {
                    rejection:
                      'Too many loans are being created right now. Please try again later.',
                    approver: 'Rate limit reached. Please try again in a few minutes.',
                  }
                : null;

          const rejectionReason =
            matched?.rejection ??
            'The loan could not be created. The request has been auto-rejected.';

          const { error: autoRejectError } = await supabase
            .from('loan_requests')
            .update({
              status: 'rejected',
              responded_at: now,
              rejection_reason: rejectionReason,
            })
            .eq('id', requestId)
            .eq('status', 'pending');

          if (autoRejectError) {
            // Auto-reject is best-effort; the approver can still reject manually.
            if (import.meta.env.DEV)
              console.error(
                '[useLoanRequests] Auto-reject after approval failure failed:',
                autoRejectError,
              );
          }

          if (matched) {
            throw new Error(matched.approver);
          }
          throw approveError;
        }
      } else {
        // Rejection is a single UPDATE with no side effects, so it does not
        // need the transactional RPC treatment.
        const { data: updatedRows, error: updateError } = await supabase
          .from('loan_requests')
          .update({
            status: 'rejected',
            responded_at: now,
          })
          .eq('id', requestId)
          .eq('status', 'pending')
          .select('id');

        if (updateError) throw updateError;
        if (!updatedRows || updatedRows.length === 0) {
          throw new Error(REQUEST_CONFLICT_MESSAGE);
        }
      }

      return { approved };
    },
    onSuccess: async (data, variables) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['loan-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['items'] }),
        queryClient.invalidateQueries({ queryKey: ['my-items'] }),
        queryClient.invalidateQueries({ queryKey: ['notifications'] }),
        queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] }),
        queryClient.invalidateQueries({ queryKey: ['group-activity'] }),
        queryClient.invalidateQueries({ queryKey: ['overdue-notifications'] }),
      ]);
      posthog.capture(data.approved ? 'loan_request_approved' : 'loan_request_rejected', {
        request_id: variables.requestId,
      });
    },
    onError: (error: any) => {
      captureSentryException(error);
      // Our mutationFn throws plain Error() with user-friendly messages for
      // known circulation failures and conflicts. Raw Supabase/Postgrest
      // errors carry a `.details` property that mapSupabaseError can parse.
      const msg = error?.details
        ? mapSupabaseError(error)
        : error?.message || mapSupabaseError(error);
      toast.error(msg);
    },
  });
}

/**
 * Cancels a pending loan request by deleting it.
 *
 * The `.eq('status', 'pending')` filter ensures only unprocessed requests can be
 * cancelled — already-approved or already-rejected requests are left untouched.
 *
 * @returns TanStack mutation accepting a `requestId` string.
 * @throws {Error} If the request is no longer pending.
 */
export function useCancelRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      await assertOnline();
      const { data, error } = await supabase
        .from('loan_requests')
        .delete()
        .eq('id', requestId)
        // [LEARN] Idempotency guard: the .eq('status', 'pending') filter ensures already-processed requests can't be cancelled, preventing race conditions.
        .eq('status', 'pending') // Guard: reject the delete if already processed
        .select('id');

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('This request can only be cancelled while it is pending.');
      }
    },
    onSuccess: (_, requestId) => {
      queryClient.invalidateQueries({ queryKey: ['loan-requests'] });
      posthog.capture('loan_request_cancelled', { request_id: requestId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Dismisses a rejected loan request by deleting it from the database.
 *
 * The `.eq('status', 'rejected')` filter ensures only rejected requests can be
 * dismissed — pending or approved requests are left untouched.
 *
 * @returns TanStack mutation accepting a `requestId` string.
 * @throws {Error} If the request is no longer in the rejected state.
 */
export function useDismissRejectedRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      await assertOnline();
      const { data, error } = await supabase
        .from('loan_requests')
        .delete()
        .eq('id', requestId)
        .eq('status', 'rejected')
        .select('id');

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('This request is no longer in the rejected state.');
      }
    },
    onSuccess: (_, requestId) => {
      queryClient.invalidateQueries({ queryKey: ['loan-requests'] });
      posthog.capture('loan_request_dismissed', { request_id: requestId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}
